import { Component, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { SigningStatus } from "@models/annual-statements/annual-statement-enums";
import { AsicStatus } from '@models/documents/asic-status.enum';
import { Document } from "@models/documents/document";
import { DocumentStatusEnum } from "@models/documents/document-status.enum";
import { DocumentStepEnum } from "@models/documents/document-step.enum";
import {
  ProviderConnectionStatus
} from "@shared/components/documents-related/sign-integrations-dropdown/model/provider-connection-status";
import {
  SignIntegrationsDropdownComponent
} from "@shared/components/documents-related/sign-integrations-dropdown/sign-integrations-dropdown.component";
import { ButtonComponent } from "@shared/controls/button/button.component";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { DocumentsService } from "@shared/services/documents.service";

@Component({
  selector: 'app-generate-documents-header',
  standalone: true,
  imports: [
    ButtonComponent,
    HasRoleDirective,
    SignIntegrationsDropdownComponent
  ],
  templateUrl: 'generate-documents-header.component.html',
  styleUrl: 'generate-documents-header.component.scss'
})
export class GenerateDocumentsHeaderComponent implements OnChanges {
  @Input() loading = false;
  @Input() currentStepIndex: number | undefined;
  @Input() document: Document | undefined;
  @Input() notBeLodgedFormTypeList: string[] | undefined;
  @Input() currentProviderConnectionStatus: ProviderConnectionStatus | null = null;

  @Output() saveAndCompleteLater = new EventEmitter<boolean>();
  @Output() confirmChanges = new EventEmitter<boolean>();
  @Output() signDocuments = new EventEmitter<boolean>();
  @Output() paperSignDocuments = new EventEmitter<boolean>();
  @Output() sendEmail = new EventEmitter<boolean>();
  @Output() lodgeDocumentNow = new EventEmitter<boolean>();
  @Output() confirmNotLodgedForm = new EventEmitter<boolean>();
  @Output() eSignConnectionChanged = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<boolean>();

  documentsService = inject(DocumentsService);
  confirmBtnName = 'Confirm the Changes';
  showConfirmTheChanges = true;
  protected readonly DocumentStepEnum = DocumentStepEnum;
  protected readonly DocumentStatusEnum = DocumentStatusEnum;
  protected readonly SigningStatus = SigningStatus;

  saveLoading = this.documentsService.saveLoading;
  patchLoading = this.documentsService.patchLoading;
  sendSignLoading= this.documentsService.sendSignLoading;
  lodgeNowLoading = this.documentsService.lodgeNowLoading;
  disabledHeaderBtn = this.documentsService.disabledHeaderBtn;
  showVoidEnvelope = this.documentsService.showVoidEnvelope;
  showSendEmailBtn = this.documentsService.showSendEmailBtn;
  showNextBtn = this.documentsService.showNextBtn;
  showSendPaperBtn = this.documentsService.showSendPaperBtn;

  showNotLodgedConfirmBtn = signal<boolean>(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentStepIndex'] || changes['showVoidEnvelope']) {
      if (this.currentStepIndex === DocumentStepEnum.GenerateDocuments) {
        this.confirmBtnName = 'Generate Documents';
      } else if (this.currentStepIndex === DocumentStepEnum.SignDocuments) {
        this.confirmBtnName = 'Sign Documents';
      } else {
        this.confirmBtnName = 'Confirm the Changes';
      }
    }

    if(changes['document'] && this.document?.type) {
      this.showNotLodgedConfirmBtn.set(!!this.notBeLodgedFormTypeList?.includes(this.document?.type ?? ''));

      // if (isTrustEstablishmentDocumentType(this.document.type)) {
      //   this.showConfirmTheChanges = false;
      // }
    }
  }

  save(): void {
    this.saveAndCompleteLater.emit(true);
  }

  confirm(): void {
    this.confirmChanges.emit(true);
  }

  confirmNotLodged(): void {
    this.confirmNotLodgedForm.emit(true);
  }

  sign(): void {
    this.signDocuments.emit(true);
  }

  paperSign(): void {
    this.paperSignDocuments.emit(true);
  }

  onSendEmail(): void {
    this.sendEmail.emit(true);
  }

  onLodgeDocumentNow(): void {
    this.lodgeDocumentNow.emit(true);
  }

  onNextStep(): void {
    this.nextStep.emit(true);
  }

  get displayLodgeNowBtn(): boolean {
    const signaturePending = this.document?.documentStatus === DocumentStatusEnum.SignaturePending || this.document?.documentStatus === DocumentStatusEnum.LodgementPending;
    const readyToSend = this.document?.documentSigning?.signingStatus === SigningStatus.ReadyToSend;
    const send = this.document?.documentSigning?.signingStatus === SigningStatus.Sent && this.document.documentSigning?.signedOn == null;

    return !this.showNotLodgedConfirmBtn() && signaturePending && (readyToSend || send);
  }

  getButtonText(): string {
    const notLodgedConfirmText = this.showNotLodgedConfirmBtn() ? 'Confirm Changes' : 'Lodge';

    if (this.showSendEmailBtn()) {
      return 'Send email';
    } else if (this.showSendPaperBtn()) {
      const isSigned = this.document?.documentSigning?.signingStatus === SigningStatus.Signed;
      return isSigned ? notLodgedConfirmText : 'Confirm Signing';
    } else if(this.document?.asicStatus != AsicStatus.NotLodged) {
      return 'Next Step';
    } else {
      return this.showVoidEnvelope() ? notLodgedConfirmText : 'Send for eSign';
    }
  }

  getButtonAction(): void {
    const notLodgedConfirmAction = this.showNotLodgedConfirmBtn() ? () => this.confirmNotLodged() : () => this.confirm();

    if (this.showSendEmailBtn()) {
      this.onSendEmail();
    } else if (this.showSendPaperBtn()) {
      const isSigned = this.document?.documentSigning?.signingStatus === SigningStatus.Signed;
      isSigned ? notLodgedConfirmAction() : this.paperSign();
    } else if(this.showNextBtn()) {
      this.onNextStep();
    } else{
      this.showVoidEnvelope() ? notLodgedConfirmAction() : this.sign();
    }
  }

  onESignConnectionChanged(): void {
    this.eSignConnectionChanged.emit();
  }

  get lodgeNowBtnLoading(): boolean {
    return this.loading || this.patchLoading() || this.saveLoading() || this.lodgeNowLoading();
  }

  get actionBtnLoading(): boolean {
    if (this.showSendEmailBtn()) {
      return this.loading || this.patchLoading() || this.sendSignLoading();
    } else {
      return this.loading || this.patchLoading() || this.sendSignLoading() || this.lodgeNowLoading();
    }
  }

  get actionBtnDisabled(): boolean {
    if(this.showNextBtn())
      return false;

    return this.showNextBtn() || this.actionBtnLoading || this.disabledHeaderBtn();
  }

  get secondLodgeBtnDisabled(): boolean{
    return this.sendSignLoading() || this.document?.documentSigning?.signedOn == null;
  }

  get showSecondLodgeBtn(): boolean {
    return this.document?.documentStatus === DocumentStatusEnum.LodgementPending
      && this.document?.documentSigning?.signingStatus === SigningStatus.MarkedAsSigned
      && this.document?.documentSigning?.signedOn != null
      && this.document?.asicStatus === AsicStatus.NotLodged
      && !this.showVoidEnvelope() && !this.showNotLodgedConfirmBtn();
  }
}
